import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceDot } from 'recharts';
import Button from '../UI/Button';

export default function PersonalizedResultsScreen({ product, userAge, onContinue }) {
  const age = userAge || 45; // Fallback if age not provided

  // Universal exponential energy decline with age - MORE DRAMATIC
  // Age 20: 100%, Age 40: 50%, Age 60: 20%, Age 80: 10%
  const chartData = [
    { age: 20, energy: 100, label: 'Age 20' },
    { age: 40, energy: 50, label: 'Age 40' },
    { age: 60, energy: 20, label: 'Age 60' },
    { age: 80, energy: 10, label: 'Age 80' }
  ];

  // Calculate user's approximate energy level based on age (exponential decay)
  const calculateEnergyLevel = (userAge) => {
    if (userAge <= 20) return 100;
    if (userAge >= 80) return 10;

    // Steeper exponential decay formula
    const decayRate = 0.038; // Increased decay constant for more dramatic curve
    return Math.max(10, 100 * Math.exp(-decayRate * (userAge - 20)));
  };

  const userEnergyLevel = Math.round(calculateEnergyLevel(age));

  // Find which age range to highlight
  const getHighlightedRange = (age) => {
    if (age < 30) return 'young';
    if (age < 50) return 'middle';
    if (age < 70) return 'mature';
    return 'senior';
  };

  const highlightedRange = getHighlightedRange(age);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-4 md:p-6 lg:p-8">
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-4">
          Your <span className="text-renuvia-gold">Cellular Energy</span> Analysis
        </h2>

        <p className="text-lg text-center text-slate mb-8">
          At age {age}, your cellular energy levels are typically around <span className="font-bold text-charcoal">{userEnergyLevel}%</span> of peak capacity.
        </p>

        {/* Energy Decline Chart */}
        <div className="bg-warm-beige rounded-xl p-2 md:p-4 lg:p-6 mb-8">
          <h3 className="text-center text-charcoal font-semibold text-lg mb-6">ENERGY LEVELS BY AGE</h3>

          <div className="h-72 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 30, right: 10, left: -10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#2D2D2D', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  label={{
                    value: 'Energy Level',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#64748b', fontSize: 11 }
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <Line
                  type="natural"
                  dataKey="energy"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={{ fill: '#ef4444', r: 6 }}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationBegin={200}
                />
                {/* User's age marker */}
                <ReferenceDot
                  x={`Age ${Math.round(age / 20) * 20}`}
                  y={chartData.find(d => d.age === Math.round(age / 20) * 20)?.energy || 50}
                  r={8}
                  fill="#C9A961"
                  stroke="#fff"
                  strokeWidth={2}
                  label={{
                    value: 'You are here',
                    position: 'top',
                    fill: '#C9A961',
                    fontSize: 12,
                    fontWeight: 'bold',
                    offset: 15
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <p className="text-center text-sm text-slate">
            Cellular energy naturally declines exponentially with age due to mitochondrial dysfunction and cellular aging.
          </p>
        </div>

        {/* Key Insight */}
        <div className="bg-gradient-to-r from-renuvia-gold/10 to-renuvia-gold/5 rounded-xl p-6 mb-8">
          <p className="text-center text-charcoal">
            <span className="font-bold">The good news?</span> Targeted supplementation can help restore and maintain optimal cellular energy levels, regardless of age.
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          See Your Transformation Timeline →
        </Button>
      </div>
    </motion.div>
  );
}
